import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { budgetService } from '../end-points/orcamento/budget-api';
import { toast } from '@/hooks/use-toast';
import { BudgetSubitemFormData } from '@/components/orcamentos/schemas';
import { BudgetLineItemFormData } from '@/types/orcamentos.interface';

interface IError {
  data?: {
    message?: string;
  };
  message?: string;
}

/**
 * Get all budgets for authenticated user
 */
export const useGetBudget = (enabled: boolean = true) => {
  return useQuery({
    queryKey: ['budgets-list'],
    queryFn: () => budgetService.getBudgets(),
    enabled,
  });
};

/**
 * Get budget by movie/project ID
 */
export const useGetBudgetByMovie = (movieId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['budget-by-movie', movieId],
    queryFn: () => budgetService.getBudgetByMovie(movieId),
    enabled: enabled && !!movieId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Get budget table data
 */
export const useGetBudgetTable = (budgetId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['budget-table', budgetId],
    queryFn: () => budgetService.getBudgetTable(budgetId),
    enabled: enabled && !!budgetId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
};

/**
 * Upload fiscal note for a budget
 */
export const useUploadNFBudget = () => {
  return useMutation({
    mutationFn: ({ data, budgetId }: { data: FormData; budgetId: number }) =>
      budgetService.uploadNFBudget(data, budgetId),
    onSuccess: () => {
      toast({
        variant: "success",
        title: "Nota fiscal enviada",
        description: "Nota fiscal enviada com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao tentar enviar nota fiscal",
        description: err.data?.message ?? "Não foi possível enviar a nota fiscal",
        variant: "destructive",
      });
    }
  });
};

/**
 * Create a new budget line item
 */
export const useCreateBudgetLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, phaseId, data }: { budgetId: number; phaseId: string; data: BudgetLineItemFormData }) =>
      budgetService.createLineItem(budgetId, phaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Item criado",
        description: "Item de orçamento criado com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao criar item",
        description: err.data?.message ?? "Não foi possível criar o item de orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Create a new budget subitem
 */
export const useCreateBudgetSubitem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, phaseId, data }: { budgetId: number; phaseId: string; data: BudgetSubitemFormData }) =>
      budgetService.createSubitem(budgetId, phaseId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      queryClient.invalidateQueries({ queryKey: ['budget-step-items'] });
      toast({
        title: "Subitem criado",
        description: "Subitem de orçamento criado com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao criar subitem",
        description: err.data?.message ?? "Não foi possível criar o subitem de orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Update an existing budget line item
 */
export const useUpdateBudgetLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, itemId, data }: { budgetId: number; itemId: string; data: BudgetLineItemFormData }) =>
      budgetService.updateLineItem(budgetId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Item atualizado",
        description: "Item de orçamento atualizado com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao atualizar item",
        description: err.data?.message ?? "Não foi possível atualizar o item de orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Update an existing budget subitem
 */
export const useUpdateBudgetSubitem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, itemId, data }: { budgetId: number; itemId: string; data: BudgetSubitemFormData }) =>
      budgetService.updateSubitem(budgetId, itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Subitem atualizado",
        description: "Subitem de orçamento atualizado com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao atualizar subitem",
        description: err.data?.message ?? "Não foi possível atualizar o subitem de orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Delete a budget line item
 */
export const useDeleteBudgetLineItem = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, itemId }: { budgetId: number; itemId: number }) =>
      budgetService.deleteLineItem(budgetId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budgets-list'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Item removido",
        description: "Item de orçamento removido com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao remover item",
        description: err.data?.message ?? "Não foi possível remover o item de orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Get current phase of a budget
 */
export const useGetCurrentPhase = (budgetId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['budget-current-phase', budgetId],
    queryFn: () => budgetService.getCurrentPhase(budgetId),
    enabled: enabled && !!budgetId,
  });
};

/**
 * Transition budget to a new phase
 */
export const useTransitionPhase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, newPhase }: { budgetId: number; newPhase: number }) =>
      budgetService.transitionPhase(budgetId, newPhase),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-current-phase'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Fase atualizada",
        description: "Fase do orçamento atualizada com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao atualizar fase",
        description: err.data?.message ?? "Não foi possível atualizar a fase do orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Get approval status of a budget
 */
export const useGetApprovalStatus = (budgetId: number, enabled: boolean = true) => {
  return useQuery({
    queryKey: ['budget-approval-status', budgetId],
    queryFn: () => budgetService.getApprovalStatus(budgetId),
    enabled: enabled && !!budgetId,
  });
};

/**
 * Approve a budget
 */
export const useApproveBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, comments }: { budgetId: number; comments?: string }) =>
      budgetService.approveBudget(budgetId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-approval-status'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Orçamento aprovado",
        description: "Orçamento aprovado com sucesso",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao aprovar orçamento",
        description: err.data?.message ?? "Não foi possível aprovar o orçamento",
        variant: "destructive",
      });
    }
  });
};

/**
 * Reject a budget
 */
export const useRejectBudget = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ budgetId, comments }: { budgetId: number; comments?: string }) =>
      budgetService.rejectBudget(budgetId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['budget-approval-status'] });
      queryClient.invalidateQueries({ queryKey: ['budget-by-movie'] });
      toast({
        title: "Orçamento rejeitado",
        description: "Orçamento rejeitado",
      });
    },
    onError: (err: IError) => {
      toast({
        title: "Erro ao rejeitar orçamento",
        description: err.data?.message ?? "Não foi possível rejeitar o orçamento",
        variant: "destructive",
      });
    }
  });
};